import { useState, useEffect, useMemo } from 'react';
import {
  useWebSocket,
  WebsocketTypeEnum,
} from '@providers/WebsocketProvider/ui/WebsocketProvider';
import { getDocumentGenerationProgress } from '@api/document/apiService';
import { getUseCaseProgress } from '@api/use-case';
import { calibrateRemainingSeconds } from './calibrate';
import {
  getExpectedDurationSeconds,
  getTaskDefinition,
  getTaskDefinitions,
  normalizeKey,
  orderTasksForPipeline,
  type Pipeline,
} from './pipelines';

interface Task {
  task_type: string;
  task_name: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  progress_percentage?: number;
  current_step?: string;
  execution_time?: number;
  started_at?: number;
  // Position in the backend's step list, sent with each event.
  step_index?: number;
}

type ProgressApiTask = Omit<Task, 'started_at'> & {
  started_at?: string | number;
  finished_at?: string | number;
};

interface GenerationProgressState {
  tasks: Task[];
  overallProgress: number;
  estimatedTimeLeft: number;
  currentTask: Task | null;
  error: any | null;
  isGenerating: boolean;
  finalStatus: 'COMPLETED' | 'FAILED' | 'CANCELLED' | null;
}

interface GenerationProgressOptions {
  enableSnapshot?: boolean;
  enablePolling?: boolean;
  enableSynthetic?: boolean;
  pipeline?: Pipeline;
}

const normalizeStatus = (value: string): Task['status'] => {
  const raw = String(value || '').toUpperCase();
  if (raw.includes('IN') && raw.includes('PROGRESS')) return 'IN_PROGRESS';
  if (raw === 'COMPLETED') return 'COMPLETED';
  if (raw === 'FAILED') return 'FAILED';
  return 'PENDING';
};

type ProgressEntry = {
  tasks: Task[];
  overallProgressFromEvent: number | null;
  estimatedTimeFromEvent: number | null;
  error: any | null;
  hasProgressSnapshot: boolean;
  hasLiveEvents: boolean;
  finalStatus: 'COMPLETED' | 'FAILED' | 'CANCELLED' | null;
  syntheticStartedAt: number | null;
  pipeline: GenerationProgressOptions['pipeline'];
  pollingDisabled: boolean;
  failedPolls?: number;
  isFetching: boolean;
  subscribers: Set<() => void>;
  poller: ReturnType<typeof setTimeout> | null;
  // Poll pacing (ZLAYT-1514 review): current delay, when polling began, when
  // the run began (from the snapshot), and the last snapshot seen.
  pollDelay?: number;
  pollStartedAt?: number | null;
  runStartedAt?: number | null;
  lastSnapshot?: string;
  optimisticTimer: ReturnType<typeof setTimeout> | null;
};

const progressStore = new Map<string, ProgressEntry>();
const STORAGE_KEY_PREFIX = 'generation_progress:';
const UNKNOWN_PIPELINE_KEY = 'unknown';

const getEntryKey = (
  documentId: string,
  pipeline?: GenerationProgressOptions['pipeline']
) => `${pipeline || UNKNOWN_PIPELINE_KEY}:${documentId}`;

const findEntryKey = (
  documentId: string,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  if (pipeline) {
    const key = getEntryKey(documentId, pipeline);
    if (progressStore.has(key)) return key;
  }
  const suffix = `:${documentId}`;
  for (const key of progressStore.keys()) {
    if (key.endsWith(suffix)) {
      return key;
    }
  }
  return null;
};

const createInitialTasks = (
  pipeline?: GenerationProgressOptions['pipeline']
): Task[] =>
  getTaskDefinitions(pipeline).map((task) => ({
    task_type: task.task_type,
    task_name: task.task_name,
    status: 'PENDING',
  }));

const alignTasksWithPipeline = (
  prevTasks: Task[],
  pipeline?: GenerationProgressOptions['pipeline']
): Task[] => {
  if (!pipeline) return prevTasks;
  const base = createInitialTasks(pipeline);
  const mapped = base.map((task) => {
    const key = normalizeKey(task.task_type || task.task_name);
    const existing = prevTasks.find(
      (prev) =>
        normalizeKey(prev.task_type) === key ||
        normalizeKey(prev.task_name) === key
    );
    return existing ? { ...task, ...existing } : task;
  });
  return mapped;
};

const setEntryPipeline = (
  documentId: string,
  entry: ProgressEntry,
  pipeline: GenerationProgressOptions['pipeline']
) => {
  const currentKey =
    findEntryKey(documentId, entry.pipeline) ||
    findEntryKey(documentId, pipeline);
  const nextKey = getEntryKey(documentId, pipeline);
  if (currentKey && currentKey !== nextKey) {
    progressStore.delete(currentKey);
  }
  progressStore.set(nextKey, entry);
  entry.pipeline = pipeline;
  entry.tasks = alignTasksWithPipeline(entry.tasks, pipeline);
};

const normalizeRestoredTasks = (
  tasks: Task[],
  pipeline?: GenerationProgressOptions['pipeline']
) =>
  alignTasksWithPipeline(
    tasks.map((task) => {
      const rawStatus = normalizeStatus(task.status || '');
      const progress = task.progress_percentage ?? 0;
      const stepText = String(task.current_step || '').toLowerCase();
      let status = rawStatus;
      if (!status || status === 'PENDING') {
        if (stepText.includes('complete') || progress >= 100) {
          status = 'COMPLETED';
        } else if (progress > 0) {
          status = 'IN_PROGRESS';
        } else {
          status = 'PENDING';
        }
      }
      return {
        ...task,
        status,
      };
    }),
    pipeline
  );

const inferStatusFromTask = (task: {
  status?: string;
  progress_percentage?: number;
  current_step?: string;
}) => {
  const rawStatus = normalizeStatus(task.status || '');
  const progress = task.progress_percentage ?? 0;
  const stepText = String(task.current_step || '').toLowerCase();

  // First check current_step and progress for definitive completion signals
  // This takes precedence over rawStatus to handle cases where backend sends
  // status='PENDING' but current_step indicates completion
  if (
    stepText.includes('complete') ||
    stepText.includes('finished') ||
    progress >= 100
  ) {
    return 'COMPLETED';
  }

  // Then check if we have an explicit non-pending status from backend
  if (rawStatus && rawStatus !== 'PENDING') {
    return rawStatus;
  }

  // Check for in-progress indicators
  if (
    stepText.includes('start') ||
    stepText.includes('processing') ||
    stepText.includes('generating') ||
    progress > 0
  ) {
    return 'IN_PROGRESS';
  }

  return 'PENDING';
};

const inferPipelineFromTask = (
  taskType?: string,
  taskName?: string
): GenerationProgressOptions['pipeline'] | undefined => {
  const key = normalizeKey(taskType || taskName || '');
  if (key.startsWith('deposition')) return 'deposition_summary';
  if (key.startsWith('usecase')) return 'use_case';
  return undefined;
};

const getStorageKey = (
  documentId: string,
  pipeline?: GenerationProgressOptions['pipeline']
) =>
  pipeline
    ? `${STORAGE_KEY_PREFIX}${pipeline}:${documentId}`
    : `${STORAGE_KEY_PREFIX}${documentId}`;

const persistEntry = (documentId: string, entry: ProgressEntry) => {
  if (!documentId) return;
  if (entry.finalStatus) {
    sessionStorage.removeItem(getStorageKey(documentId, entry.pipeline));
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${documentId}`);
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}unknown:${documentId}`);
    return;
  }
  try {
    const payload = {
      tasks: entry.tasks,
      overallProgressFromEvent: entry.overallProgressFromEvent,
      estimatedTimeFromEvent: entry.estimatedTimeFromEvent,
      pipeline: entry.pipeline,
      updatedAt: Date.now(),
    };
    sessionStorage.setItem(
      getStorageKey(documentId, entry.pipeline),
      JSON.stringify(payload)
    );
    if (!entry.pipeline) {
      sessionStorage.setItem(
        `${STORAGE_KEY_PREFIX}${documentId}`,
        JSON.stringify(payload)
      );
    }
  } catch {
    // Ignore storage failures.
  }
};

const restoreEntry = (
  documentId: string,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  if (!documentId) return null;
  try {
    const raw =
      sessionStorage.getItem(getStorageKey(documentId, pipeline)) ||
      sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${documentId}`) ||
      sessionStorage.getItem(`${STORAGE_KEY_PREFIX}unknown:${documentId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.pipeline && pipeline && parsed.pipeline !== pipeline) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const ensureEntry = (
  documentId: string,
  pipeline?: GenerationProgressOptions['pipeline']
): ProgressEntry => {
  const directKey = pipeline ? getEntryKey(documentId, pipeline) : null;
  if (directKey && progressStore.has(directKey)) {
    return progressStore.get(directKey)!;
  }

  const existingKey = findEntryKey(documentId);
  if (existingKey) {
    const existing = progressStore.get(existingKey)!;
    const wantsPipeline = pipeline || existing.pipeline;
    const restored = restoreEntry(documentId, wantsPipeline);
    if (wantsPipeline && existing.pipeline !== wantsPipeline) {
      // If document is already finalized, don't reset anything - just update the pipeline key
      if (existing.finalStatus) {
        setEntryPipeline(documentId, existing, wantsPipeline);
        return existing;
      }

      // Preserve existing state flags to prevent progress resets
      const preservedHasLiveEvents = existing.hasLiveEvents;
      const preservedHasProgressSnapshot = existing.hasProgressSnapshot;
      const preservedSyntheticStartedAt = existing.syntheticStartedAt;
      const preservedOverallProgress = existing.overallProgressFromEvent;
      const preservedEstimatedTime = existing.estimatedTimeFromEvent;

      if (restored) {
        existing.tasks = normalizeRestoredTasks(
          restored.tasks as Task[],
          wantsPipeline
        );
        existing.overallProgressFromEvent =
          typeof restored.overallProgressFromEvent === 'number'
            ? restored.overallProgressFromEvent
            : preservedOverallProgress;
        existing.estimatedTimeFromEvent =
          typeof restored.estimatedTimeFromEvent === 'number'
            ? restored.estimatedTimeFromEvent
            : preservedEstimatedTime;
        // Preserve live events flag to prevent synthetic progress from overwriting real data
        existing.hasLiveEvents = preservedHasLiveEvents || false;
        existing.hasProgressSnapshot = preservedHasProgressSnapshot || false;
        existing.finalStatus = null;
        // Preserve syntheticStartedAt to maintain progress continuity
        existing.syntheticStartedAt = preservedSyntheticStartedAt;
      } else {
        existing.tasks = createInitialTasks(wantsPipeline);
        existing.overallProgressFromEvent = preservedOverallProgress;
        existing.estimatedTimeFromEvent = preservedEstimatedTime;
        existing.hasLiveEvents = preservedHasLiveEvents || false;
        existing.hasProgressSnapshot = preservedHasProgressSnapshot || false;
        existing.finalStatus = null;
        existing.syntheticStartedAt = preservedSyntheticStartedAt;
      }
      setEntryPipeline(documentId, existing, wantsPipeline);
      persistEntry(documentId, existing);
    } else if (
      restored &&
      !existing.finalStatus &&
      !existing.hasLiveEvents &&
      !existing.hasProgressSnapshot &&
      existing.tasks.every((task) => task.status === 'PENDING')
    ) {
      existing.tasks = normalizeRestoredTasks(
        restored.tasks as Task[],
        wantsPipeline
      );
      existing.overallProgressFromEvent =
        typeof restored.overallProgressFromEvent === 'number'
          ? restored.overallProgressFromEvent
          : null;
      existing.estimatedTimeFromEvent =
        typeof restored.estimatedTimeFromEvent === 'number'
          ? restored.estimatedTimeFromEvent
          : null;
      // Never restore finalStatus - always start fresh for new generations
      existing.finalStatus = null;
      existing.pipeline = wantsPipeline;
      setEntryPipeline(documentId, existing, wantsPipeline);
      persistEntry(documentId, existing);
    }
    return existing;
  }

  const restored = restoreEntry(documentId, pipeline);
  const restoredPipeline =
    pipeline || (restored?.pipeline as GenerationProgressOptions['pipeline']);
  const restoredTasks = Array.isArray(restored?.tasks)
    ? normalizeRestoredTasks(restored.tasks as Task[], restoredPipeline)
    : createInitialTasks(restoredPipeline);

  const entry: ProgressEntry = {
    tasks: restoredTasks,
    overallProgressFromEvent:
      typeof restored?.overallProgressFromEvent === 'number'
        ? restored.overallProgressFromEvent
        : null,
    estimatedTimeFromEvent:
      typeof restored?.estimatedTimeFromEvent === 'number'
        ? restored.estimatedTimeFromEvent
        : null,
    error: null,
    hasProgressSnapshot: false,
    hasLiveEvents: false,
    finalStatus: null,
    syntheticStartedAt: null,
    pipeline: restoredPipeline,
    pollingDisabled: false,
    isFetching: false,
    subscribers: new Set(),
    poller: null,
    optimisticTimer: null,
  };

  progressStore.set(getEntryKey(documentId, restoredPipeline), entry);
  if (restored) {
    persistEntry(documentId, entry);
  }
  return entry;
};

const notifySubscribers = (entry: ProgressEntry | null) => {
  if (!entry) return;
  entry.subscribers.forEach((callback) => callback());
};

const mergeTaskUpdate = (
  prevTasks: Task[],
  taskData: any,
  pipeline?: GenerationProgressOptions['pipeline'],
  options: { preferExisting?: boolean } = {}
): Task[] => {
  const taskKey = normalizeKey(taskData.task_type || taskData.task_name);
  const existingIndex = prevTasks.findIndex((task) => {
    return (
      normalizeKey(task.task_type) === taskKey ||
      normalizeKey(task.task_name) === taskKey
    );
  });
  const definition = getTaskDefinition(
    taskData.task_type,
    taskData.task_name,
    pipeline
  );
  const status = inferStatusFromTask(taskData);
  const now = Date.now();
  const existingTask = existingIndex >= 0 ? prevTasks[existingIndex] : null;
  const startedAtPayload =
    taskData.started_at !== undefined && taskData.started_at !== null
      ? typeof taskData.started_at === 'number'
        ? taskData.started_at
        : new Date(taskData.started_at).getTime()
      : undefined;

  const incomingProgress = taskData.progress_percentage;
  // Preserve existing progress if incoming progress is undefined or 0, but only if status hasn't changed
  const progressPercentage =
    status === 'COMPLETED'
      ? 100
      : status === 'IN_PROGRESS' && incomingProgress === undefined
        ? existingTask?.progress_percentage
        : status === 'IN_PROGRESS' &&
            incomingProgress === 0 &&
            existingTask?.status === 'IN_PROGRESS' &&
            (existingTask?.progress_percentage ?? 0) > 0
          ? existingTask.progress_percentage
          : (incomingProgress ?? existingTask?.progress_percentage);

  const incomingStepIndex =
    typeof taskData.metadata?.step_index === 'number'
      ? taskData.metadata.step_index
      : typeof taskData.step_index === 'number'
        ? taskData.step_index
        : existingTask?.step_index;

  const newTask: Task = {
    task_type: taskData.task_type || definition?.task_type || '',
    task_name: taskData.task_name || definition?.task_name || '',
    status,
    progress_percentage: progressPercentage,
    current_step: taskData.current_step,
    execution_time: taskData.execution_time,
    step_index: incomingStepIndex,
    started_at:
      status === 'IN_PROGRESS'
        ? (existingTask?.started_at ?? startedAtPayload ?? now)
        : (existingTask?.started_at ?? startedAtPayload),
  };

  if (existingIndex >= 0) {
    if (options.preferExisting && existingTask) {
      const existingProgress = existingTask.progress_percentage ?? 0;
      const incomingProgress = progressPercentage ?? 0;
      const isRegression =
        (existingTask.status === 'COMPLETED' && status !== 'COMPLETED') ||
        (existingTask.status === 'IN_PROGRESS' && status === 'PENDING') ||
        (existingTask.status === 'IN_PROGRESS' &&
          status === 'IN_PROGRESS' &&
          incomingProgress < existingProgress);
      if (isRegression) {
        return prevTasks;
      }
    }
    const updated = [...prevTasks];
    updated[existingIndex] = {
      ...updated[existingIndex],
      ...newTask,
    };
    return updated;
  }

  return [...prevTasks, newTask];
};

const updateTaskInStore = (
  documentId: string,
  taskData: any,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  const entry = ensureEntry(documentId, pipeline);

  // If document is already finalized, ignore late task updates to prevent state corruption
  if (entry.finalStatus) {
    return;
  }

  const inferred = inferPipelineFromTask(
    taskData?.task_type,
    taskData?.task_name
  );
  if (inferred && inferred !== entry.pipeline) {
    setEntryPipeline(documentId, entry, inferred);
  }
  // The poll keeps running alongside live events: they can stop arriving
  // (ZLAYT-1514). Only a final status stops it.
  entry.hasLiveEvents = true;
  entry.tasks = mergeTaskUpdate(entry.tasks, taskData, entry.pipeline);
  persistEntry(documentId, entry);
  notifySubscribers(entry);
};

const updateOverallInStore = (
  documentId: string,
  overall?: number,
  remaining?: number,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  const entry = ensureEntry(documentId, pipeline);
  entry.hasLiveEvents = true;
  if (entry.finalStatus) {
    entry.finalStatus = null;
  }
  if (overall !== undefined) {
    entry.overallProgressFromEvent = overall;
  }
  if (remaining !== undefined) {
    entry.estimatedTimeFromEvent = remaining;
  }
  persistEntry(documentId, entry);
  notifySubscribers(entry);
};

const updateErrorInStore = (
  documentId: string,
  error: any,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  const entry = ensureEntry(documentId, pipeline);
  entry.hasLiveEvents = true;
  if (entry.poller) {
    clearTimeout(entry.poller);
    entry.poller = null;
  }
  entry.error = error;
  persistEntry(documentId, entry);
  notifySubscribers(entry);
};

const finalizeDocument = (
  documentId: string,
  status: 'COMPLETED' | 'FAILED' | 'CANCELLED',
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  const entry = ensureEntry(documentId, pipeline);
  entry.hasLiveEvents = true;
  if (entry.poller) {
    clearTimeout(entry.poller);
    entry.poller = null;
  }
  entry.finalStatus = status;
  entry.tasks = entry.tasks.map((task) => {
    if (status === 'COMPLETED') {
      return {
        ...task,
        status: 'COMPLETED',
        progress_percentage: 100,
      };
    }
    if (task.status === 'COMPLETED') {
      return task;
    }
    return {
      ...task,
      status: status === 'CANCELLED' ? 'PENDING' : 'FAILED',
    };
  });
  entry.overallProgressFromEvent =
    status === 'COMPLETED' ? 100 : entry.overallProgressFromEvent;
  entry.estimatedTimeFromEvent = 0;
  persistEntry(documentId, entry);
  notifySubscribers(entry);
};

const clearEntryTimers = (entry: ProgressEntry) => {
  if (entry.poller) {
    clearTimeout(entry.poller);
    entry.poller = null;
  }
  if (entry.optimisticTimer) {
    clearTimeout(entry.optimisticTimer);
    entry.optimisticTimer = null;
  }
};

const subscribeToEntry = (
  documentId: string,
  callback: () => void,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  const entry = ensureEntry(documentId, pipeline);
  entry.subscribers.add(callback);

  return () => {
    entry.subscribers.delete(callback);
    if (entry.subscribers.size === 0) {
      clearEntryTimers(entry);
      const key =
        findEntryKey(documentId, entry.pipeline) ||
        findEntryKey(documentId, pipeline);
      // Only drop the entry this subscription belongs to. After a cancel the
      // document gets a new entry, and the last reader of the old one must
      // not delete the new one from under the popup (ZLAYT-1393).
      if (key && progressStore.get(key) === entry) {
        progressStore.delete(key);
      }
    }
  };
};

const ensureOptimisticProgress = (
  documentId: string,
  enabled: boolean,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  if (!enabled) return;
  const entry = ensureEntry(documentId, pipeline);
  if (
    entry.optimisticTimer ||
    entry.hasProgressSnapshot ||
    entry.hasLiveEvents ||
    entry.finalStatus
  )
    return;
  if (entry.tasks.some((task) => task.status !== 'PENDING')) return;

  entry.optimisticTimer = setTimeout(() => {
    entry.optimisticTimer = null;
    if (
      entry.hasProgressSnapshot ||
      entry.hasLiveEvents ||
      entry.finalStatus ||
      entry.syntheticStartedAt
    )
      return;
    if (entry.tasks.some((task) => task.status !== 'PENDING')) return;

    entry.syntheticStartedAt = Date.now();
    applySyntheticProgress(documentId, pipeline);
  }, 2500);
};

const applySyntheticProgress = (
  documentId: string,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  const entry = ensureEntry(documentId, pipeline);
  if (entry.hasLiveEvents || entry.hasProgressSnapshot || entry.finalStatus) {
    return;
  }
  if (!entry.syntheticStartedAt) {
    entry.syntheticStartedAt = Date.now();
  }

  const tasks: Task[] = entry.tasks.length
    ? entry.tasks
    : createInitialTasks(entry.pipeline);
  const startedAt = entry.syntheticStartedAt;
  const elapsedSeconds = Math.max(0, (Date.now() - startedAt) / 1000);
  const taskDurations = tasks.map((task, index) => {
    const definition = getTaskDefinitions(entry.pipeline)[index];
    return getExpectedDurationSeconds(
      task.task_type || definition?.task_type,
      task.task_name || definition?.task_name,
      entry.pipeline
    );
  });
  const totalDuration = Math.max(
    1,
    taskDurations.reduce((sum, value) => sum + value, 0)
  );
  const cappedElapsed = Math.min(
    elapsedSeconds,
    Math.max(1, totalDuration - 5)
  );

  let remaining = cappedElapsed;
  const updatedTasks: Task[] = tasks.map((task, index) => {
    // Don't override tasks that have received real backend updates
    // Check if task has explicit progress or completed/failed status from backend
    const hasRealData =
      task.status === 'COMPLETED' ||
      task.status === 'FAILED' ||
      (task.progress_percentage !== undefined && task.progress_percentage > 0);

    if (hasRealData) {
      // Preserve real task data, just update remaining time calculation
      const duration = taskDurations[index] || 60;
      if (task.status === 'COMPLETED' || task.status === 'FAILED') {
        remaining = Math.max(0, remaining - duration);
      } else if (task.status === 'IN_PROGRESS') {
        const percentComplete = (task.progress_percentage ?? 0) / 100;
        remaining = Math.max(0, remaining - duration * percentComplete);
      }
      return task;
    }

    // Only apply synthetic progress to tasks without real data
    const duration = taskDurations[index] || 60;
    if (remaining <= 0) {
      return {
        ...task,
        status: 'PENDING',
        progress_percentage: 0,
      };
    }

    if (remaining >= duration) {
      remaining -= duration;
      return {
        ...task,
        status: 'COMPLETED',
        progress_percentage: 100,
        started_at: task.started_at ?? startedAt,
      };
    }

    const progress = Math.max(1, Math.round((remaining / duration) * 90));
    remaining = 0;
    return {
      ...task,
      status: 'IN_PROGRESS',
      progress_percentage: progress,
      started_at: task.started_at ?? startedAt,
    };
  });

  entry.tasks = updatedTasks;
  persistEntry(documentId, entry);
  notifySubscribers(entry);
};

// Polls in a row that may fail before polling stops (ZLAYT-1514). One failed
// poll used to stop it for good, so a single 404 or 502 froze the bar.
const MAX_FAILED_POLLS = 5;
// The poll starts every 4 s and slows to every 30 s while nothing changes.
const POLL_MIN_MS = 4000;
const POLL_MAX_MS = 30000;
// A report run is hard-stopped at 2,100 s (use_case/tasks/instance.py).
// Past that plus a margin nothing will move, and production has runs stuck
// in Pending for days, so the poll stops (ZLAYT-1514 review).
const POLL_CEILING_MS = (2100 + 600) * 1000;

const isTabHidden = () =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden'
    ? true
    : typeof document !== 'undefined' && !!document.hidden;

const stopPolling = (entry: ProgressEntry) => {
  if (entry.poller) {
    clearTimeout(entry.poller);
    entry.poller = null;
  }
};

const pastCeiling = (entry: ProgressEntry) => {
  const since = entry.runStartedAt ?? entry.pollStartedAt;
  return since != null && Date.now() - since > POLL_CEILING_MS;
};

// Whether this entry is still the one in the store and someone still reads
// it. A poll that finishes after the last reader unmounted must not recreate
// the entry (ensureEntry would) or start a timer nobody will clear.
const isLiveEntry = (entry: ProgressEntry, documentId: string) =>
  entry.subscribers.size > 0 &&
  progressStore.get(
    findEntryKey(documentId, entry.pipeline) ?? getEntryKey(documentId)
  ) === entry;

const scheduleNextPoll = (
  documentId: string,
  entry: ProgressEntry,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  stopPolling(entry);
  if (!isLiveEntry(entry, documentId)) return;
  if (entry.finalStatus || entry.pollingDisabled) return;
  if (pastCeiling(entry)) {
    entry.pollingDisabled = true;
    return;
  }
  entry.poller = setTimeout(() => {
    entry.poller = null;
    if (!isLiveEntry(entry, documentId)) return;
    // A hidden tab asks nothing; it checks again at the same pace.
    if (isTabHidden()) {
      scheduleNextPoll(documentId, entry, pipeline);
      return;
    }
    fetchProgressSnapshot(documentId, true, pipeline);
  }, entry.pollDelay ?? POLL_MIN_MS);
};

const allStepsFinished = (tasks: ProgressApiTask[]) =>
  tasks.length > 0 &&
  tasks.every((t) => {
    const status = normalizeStatus(String(t.status));
    return status === 'COMPLETED' || status === 'FAILED';
  });

const fetchProgressSnapshot = async (
  documentId: string,
  enablePolling: boolean,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  const entry = ensureEntry(documentId, pipeline);
  if (entry.isFetching || entry.pollingDisabled || entry.finalStatus) return;
  if (enablePolling && entry.pollStartedAt == null) {
    entry.pollStartedAt = Date.now();
  }

  entry.isFetching = true;
  try {
    // Use appropriate API based on pipeline
    let data: any;
    if (pipeline === 'use_case') {
      data = await getUseCaseProgress(Number(documentId));
    } else {
      data = await getDocumentGenerationProgress(documentId);
    }
    // Nobody reads this entry any more: drop the answer, write nothing.
    if (!isLiveEntry(entry, documentId)) return;
    entry.failedPolls = 0;

    // Back off while nothing changes; go back to the quick pace on a change.
    const signature = JSON.stringify([
      data?.tasks,
      data?.overall_percentage,
      data?.status,
    ]);
    entry.pollDelay =
      signature === entry.lastSnapshot
        ? Math.min(
            POLL_MAX_MS,
            Math.round((entry.pollDelay ?? POLL_MIN_MS) * 1.5)
          )
        : POLL_MIN_MS;
    entry.lastSnapshot = signature;
    if (data?.started_at) {
      const started = new Date(data.started_at).getTime();
      if (Number.isFinite(started)) entry.runStartedAt = started;
    }

    // An empty task list is "nothing recorded yet", not a snapshot: it must
    // not switch off the time-based estimate.
    const tasks: ProgressApiTask[] = Array.isArray(data?.tasks)
      ? data.tasks
      : [];
    if (tasks.length) {
      // Live events can stop arriving; the snapshot never moves a step
      // backwards (mergeTaskUpdate, preferExisting).
      entry.tasks = tasks.reduce<Task[]>(
        (acc, task) =>
          mergeTaskUpdate(acc, task, entry.pipeline, {
            preferExisting: entry.hasLiveEvents,
          }),
        entry.tasks
      );
      entry.hasProgressSnapshot = true;
      if (
        data?.overall_percentage !== undefined &&
        (entry.overallProgressFromEvent === null ||
          data.overall_percentage >= entry.overallProgressFromEvent)
      ) {
        entry.overallProgressFromEvent = data.overall_percentage;
      }
      if (data?.estimated_time_remaining) {
        entry.estimatedTimeFromEvent = data.estimated_time_remaining;
      }
    }

    // The use case snapshot carries the run's status, so a row reloaded
    // after the run ended, or a tab that missed the final event, ends too.
    // Draft is a cancelled run (a cancel puts the version back to Draft).
    const runStatus = String(data?.status || '').toLowerCase();
    if (runStatus === 'failed') {
      const reason = data?.error || '';
      // A demand letter snapshot carries no reason; it must not replace the
      // reason a socket status already brought (ZLAYT-1642).
      if (reason || !entry.error) {
        updateErrorInStore(
          documentId,
          { status: 'Failed', message: reason, error: reason },
          pipeline
        );
      }
      finalizeDocument(documentId, 'FAILED', pipeline);
      return;
    }
    if (runStatus === 'completed') {
      finalizeDocument(documentId, 'COMPLETED', pipeline);
      return;
    }
    if (runStatus === 'draft' || runStatus.includes('cancel')) {
      finalizeDocument(documentId, 'CANCELLED', pipeline);
      return;
    }
    // A demand letter snapshot has no run status: it is over when every
    // step is. Its final status still comes from the socket or the list.
    if (!runStatus && allStepsFinished(tasks)) {
      entry.pollingDisabled = true;
    }
    notifySubscribers(entry);
  } catch (error) {
    entry.failedPolls = (entry.failedPolls || 0) + 1;
    entry.pollDelay = Math.min(
      POLL_MAX_MS,
      Math.round((entry.pollDelay ?? POLL_MIN_MS) * 1.5)
    );
    if (entry.failedPolls >= MAX_FAILED_POLLS) {
      entry.pollingDisabled = true;
    }
  } finally {
    entry.isFetching = false;
    if (enablePolling) {
      scheduleNextPoll(documentId, entry, pipeline);
    } else if (entry.pollingDisabled || entry.finalStatus) {
      stopPolling(entry);
    }
  }
};

const getSnapshot = (
  documentId: string,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  if (!documentId) {
    return {
      tasks: [],
      overallProgressFromEvent: null,
      estimatedTimeFromEvent: null,
      error: null,
      finalStatus: null,
    };
  }

  const entry = ensureEntry(documentId, pipeline);
  return {
    tasks: entry.tasks,
    overallProgressFromEvent: entry.overallProgressFromEvent,
    estimatedTimeFromEvent: entry.estimatedTimeFromEvent,
    error: entry.error,
    finalStatus: entry.finalStatus,
  };
};

const tickSubscribers = new Set<() => void>();
let tickInterval: ReturnType<typeof setInterval> | null = null;
// One "now" per tick, shared by every reader, so the My Documents row and the
// progress popup work out time-based progress from the same instant.
let progressNow = Date.now();

const subscribeTicker = (callback: () => void) => {
  tickSubscribers.add(callback);
  if (!tickInterval) {
    progressNow = Date.now();
    tickInterval = setInterval(() => {
      progressNow = Date.now();
      tickSubscribers.forEach((listener) => listener());
    }, 2000);
  }

  return () => {
    tickSubscribers.delete(callback);
    if (tickSubscribers.size === 0 && tickInterval) {
      clearInterval(tickInterval);
      tickInterval = null;
    }
  };
};

// Pipelines whose running steps show time-based (projected) progress until
// the backend reports a percentage. This is decided per pipeline, not per
// caller: the row and the popup used to pass different options and so showed
// different numbers for the same document (ZLAYT-1393).
const PROJECTED_PROGRESS_PIPELINES: GenerationProgressOptions['pipeline'][] = [
  'demand_letter',
  'use_case',
];

const computeTaskProgress = (
  task: Task,
  pipeline: GenerationProgressOptions['pipeline'],
  now: number
) => {
  if (task.status === 'COMPLETED') return 100;
  if (task.status === 'FAILED') return 100;
  if (task.progress_percentage !== undefined && task.progress_percentage > 0) {
    return Math.max(0, Math.min(100, task.progress_percentage));
  }
  if (task.status !== 'IN_PROGRESS') return 0;
  if (!PROJECTED_PROGRESS_PIPELINES.includes(pipeline)) return 0;
  // If the task has progress_percentage defined (even 0), trust it over the
  // projection.
  if (task.progress_percentage !== undefined) {
    return Math.max(0, Math.min(100, task.progress_percentage));
  }
  const expectedSeconds = getExpectedDurationSeconds(
    task.task_type,
    task.task_name,
    pipeline
  );
  const startedAt = task.started_at || now;
  const elapsedSeconds = Math.max(0, (now - startedAt) / 1000);
  const projected = Math.min(90, (elapsedSeconds / expectedSeconds) * 90);
  return Math.max(1, Math.round(projected));
};

/**
 * The overall percentage for a document: one calculation, from the store's
 * entry and the shared clock, so every reader shows the same number.
 */
const computeOverallProgress = (
  tasks: Task[],
  overallProgressFromEvent: number | null,
  finalStatus: ProgressEntry['finalStatus'],
  pipeline: GenerationProgressOptions['pipeline'],
  now: number
) => {
  if (finalStatus === 'COMPLETED') return 100;
  let calculated = 0;
  if (tasks.length) {
    let completedWeight = 0;
    let total = 0;
    tasks.forEach((task) => {
      const definition = getTaskDefinition(
        task.task_type,
        task.task_name,
        pipeline
      );
      const weight = definition?.weight ?? 5;
      total += weight;
      completedWeight +=
        (weight * computeTaskProgress(task, pipeline, now)) / 100;
    });
    if (total > 0) {
      calculated = Math.min(100, Math.round((completedWeight / total) * 100));
    }
  }
  if (overallProgressFromEvent === null) return calculated;
  return Math.max(overallProgressFromEvent, calculated);
};

/**
 * Custom hook for tracking document generation progress
 * Subscribes to WebSocket events and manages progress state
 *
 * Usage:
 * const { tasks, overallProgress, estimatedTimeLeft, error, isGenerating } =
 *   useGenerationProgress(documentId);
 */
export const useGenerationProgress = (
  documentId: string,
  options: GenerationProgressOptions = {}
): GenerationProgressState => {
  const {
    enableSnapshot = true,
    enablePolling = false,
    enableSynthetic = true,
    // Projected progress is decided per pipeline
    // (PROJECTED_PROGRESS_PIPELINES), not per caller, so every reader agrees.
    pipeline = 'demand_letter',
  } = options;
  const wsMessage = useWebSocket();
  const [snapshot, setSnapshot] = useState(() =>
    getSnapshot(documentId, pipeline)
  );
  const [tick, setTick] = useState(0);

  useEffect(() => {
    return subscribeTicker(() => {
      setTick((prev) => prev + 1);
      // Re-read the store on every tick, not only when the entry this hook
      // subscribed to notifies: after a cancel and a new generation the
      // document has a new entry, and a row still subscribed to the old one
      // froze at its old percentage (ZLAYT-1393).
      setSnapshot(getSnapshot(documentId, pipeline));
    });
  }, [documentId, pipeline]);

  useEffect(() => {
    if (!documentId || !enableSynthetic) return;
    applySyntheticProgress(documentId, pipeline);
  }, [documentId, tick, enableSynthetic, pipeline]);

  useEffect(() => {
    if (!documentId) {
      setSnapshot(getSnapshot('', pipeline));
      return;
    }

    const unsubscribe = subscribeToEntry(
      documentId,
      () => {
        setSnapshot(getSnapshot(documentId, pipeline));
      },
      pipeline
    );

    ensureEntry(documentId, pipeline);
    setSnapshot(getSnapshot(documentId, pipeline));
    ensureOptimisticProgress(documentId, enableSynthetic, pipeline);
    if (enableSnapshot) {
      fetchProgressSnapshot(documentId, enablePolling, pipeline);
    }

    return unsubscribe;
  }, [documentId, enableSnapshot, enablePolling, enableSynthetic, pipeline]);

  useEffect(() => {
    if (!wsMessage) return;

    const { type, payload } = wsMessage;

    const payloadDocumentId =
      payload?.document_id !== undefined && payload?.document_id !== null
        ? String(payload.document_id)
        : null;
    const requiresDocumentId = [
      WebsocketTypeEnum.TASK_PROGRESS,
      WebsocketTypeEnum.GENERATION_PROGRESS,
      WebsocketTypeEnum.DOCUMENT_INSTANCE_STATUS,
      WebsocketTypeEnum.DEPOSITION_SUMMARY_VERSION_STATUS,
      WebsocketTypeEnum.USE_CASE_VERSION_STATUS,
    ].includes(type as WebsocketTypeEnum);

    if (requiresDocumentId && !payloadDocumentId) {
      return;
    }

    if (payloadDocumentId && payloadDocumentId !== String(documentId)) {
      return;
    }

    if (type === WebsocketTypeEnum.TASK_PROGRESS && payload) {
      const inferred = inferPipelineFromTask(
        payload?.task_type,
        payload?.task_name
      );
      if (inferred && inferred !== pipeline) return;
    }

    if (
      type === WebsocketTypeEnum.DOCUMENT_INSTANCE_STATUS &&
      pipeline !== 'demand_letter'
    ) {
      return;
    }
    if (
      type === WebsocketTypeEnum.DEPOSITION_SUMMARY_VERSION_STATUS &&
      pipeline !== 'deposition_summary'
    ) {
      return;
    }
    if (
      type === WebsocketTypeEnum.USE_CASE_VERSION_STATUS &&
      pipeline !== 'use_case'
    ) {
      return;
    }
    if (
      type === WebsocketTypeEnum.GENERATION_PROGRESS &&
      pipeline !== 'demand_letter'
    ) {
      return;
    }

    switch (type) {
      case WebsocketTypeEnum.TASK_PROGRESS:
        if (payload) {
          updateTaskInStore(
            payloadDocumentId || String(documentId),
            payload,
            pipeline
          );
        }
        break;
      case WebsocketTypeEnum.GENERATION_PROGRESS:
        updateOverallInStore(
          payloadDocumentId || String(documentId),
          payload?.overall_percentage,
          payload?.estimated_time_remaining,
          pipeline
        );
        break;
      case WebsocketTypeEnum.DOCUMENT_INSTANCE_STATUS:
      case WebsocketTypeEnum.DEPOSITION_SUMMARY_VERSION_STATUS:
      case WebsocketTypeEnum.USE_CASE_VERSION_STATUS: {
        if (!payload?.status) break;
        const normalizedStatus = String(payload.status).toLowerCase();
        const targetId = payloadDocumentId || String(documentId);
        if (
          normalizedStatus.includes('cancel') ||
          normalizedStatus === 'draft'
        ) {
          // Draft status after cancellation
          updateErrorInStore(targetId, payload, pipeline);
          finalizeDocument(targetId, 'CANCELLED', pipeline);
        } else if (normalizedStatus.includes('failed')) {
          updateErrorInStore(targetId, payload, pipeline);
          finalizeDocument(targetId, 'FAILED', pipeline);
        } else if (normalizedStatus.includes('completed')) {
          finalizeDocument(targetId, 'COMPLETED', pipeline);
        }
        break;
      }
      default:
        break;
    }
  }, [wsMessage, documentId]);

  const orderedTasks = useMemo(() => {
    const sorted = orderTasksForPipeline(snapshot.tasks, pipeline);

    // When finalStatus is COMPLETED, force all tasks to show as COMPLETED
    // This is the simplest and most reliable way to ensure correct display
    if (snapshot.finalStatus === 'COMPLETED') {
      return sorted.map((task) => ({
        ...task,
        status: 'COMPLETED' as const,
        progress_percentage: 100,
      }));
    }

    // When finalStatus is FAILED, mark incomplete tasks appropriately
    if (snapshot.finalStatus === 'FAILED') {
      let foundIncomplete = false;
      return sorted.map((task) => {
        if (task.status === 'COMPLETED') {
          return task;
        }
        if (!foundIncomplete) {
          foundIncomplete = true;
          return {
            ...task,
            status: 'FAILED' as const,
          };
        }
        return {
          ...task,
          status: 'PENDING' as const,
        };
      });
    }

    return sorted;
  }, [snapshot.tasks, snapshot.finalStatus, pipeline]);

  const overallProgress = useMemo(
    () =>
      computeOverallProgress(
        orderedTasks,
        snapshot.overallProgressFromEvent,
        snapshot.finalStatus,
        pipeline,
        progressNow
      ),
    // tick: progressNow moves with the ticker.
    [
      orderedTasks,
      snapshot.overallProgressFromEvent,
      snapshot.finalStatus,
      pipeline,
      tick,
    ]
  );

  const rawEstimatedTimeLeft = useMemo(() => {
    if (snapshot.estimatedTimeFromEvent !== null) {
      return snapshot.estimatedTimeFromEvent;
    }
    if (!orderedTasks.length) return 0;
    let remaining = 0;
    orderedTasks.forEach((task) => {
      const expectedSeconds = getExpectedDurationSeconds(
        task.task_type,
        task.task_name,
        pipeline
      );
      if (task.status === 'COMPLETED' || task.status === 'FAILED') return;
      if (task.status === 'PENDING') {
        remaining += expectedSeconds;
        return;
      }
      const progress = computeTaskProgress(task, pipeline, progressNow);
      remaining += expectedSeconds * (1 - progress / 100);
    });
    return Math.max(0, Math.round(remaining));
    // tick: progressNow moves with the ticker.
  }, [orderedTasks, snapshot.estimatedTimeFromEvent, pipeline, tick]);

  // Cap the estimate by the pace the server has reported since the first
  // task started. overallProgress may include synthetic progress, so the
  // server figure is passed on its own (null until the first event). The
  // ticker above re-renders this hook every second, so elapsedSeconds and
  // the capped value keep moving between events.
  const firstStartedAt = orderedTasks.reduce<number | null>((min, task) => {
    const t = typeof task.started_at === 'number' ? task.started_at : null;
    return t !== null && (min === null || t < min) ? t : min;
  }, null);
  const elapsedSeconds =
    firstStartedAt !== null
      ? Math.max(0, (Date.now() - firstStartedAt) / 1000)
      : 0;
  const estimatedTimeLeft = calibrateRemainingSeconds(
    rawEstimatedTimeLeft,
    snapshot.finalStatus === 'COMPLETED'
      ? 100
      : snapshot.overallProgressFromEvent,
    elapsedSeconds
  );

  const currentTask =
    orderedTasks.find((task) => task.status === 'IN_PROGRESS') || null;
  const isGenerating =
    !snapshot.finalStatus &&
    orderedTasks.some(
      (task) => task.status === 'IN_PROGRESS' || task.status === 'PENDING'
    );

  return {
    tasks: orderedTasks,
    overallProgress,
    estimatedTimeLeft,
    currentTask,
    error: snapshot.error,
    isGenerating,
    finalStatus: snapshot.finalStatus,
  };
};

export const setDocumentFinalStatus = (
  documentId: string,
  status: 'COMPLETED' | 'FAILED',
  payload?: any,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  if (status === 'FAILED' && payload) {
    updateErrorInStore(documentId, payload, pipeline);
  }
  finalizeDocument(documentId, status, pipeline);
};

export const clearDocumentProgress = (
  documentId: string,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  if (!documentId) return;
  const keysToRemove: string[] = [];

  // Always check for ALL possible keys for this documentId to ensure complete cleanup
  const suffix = `:${documentId}`;
  for (const key of progressStore.keys()) {
    if (key.endsWith(suffix)) {
      keysToRemove.push(key);
    }
  }

  // Also add the specific pipeline key if provided
  if (pipeline) {
    const pipelineKey = getEntryKey(documentId, pipeline);
    if (!keysToRemove.includes(pipelineKey)) {
      keysToRemove.push(pipelineKey);
    }
  }

  // Fallback to default key if nothing found
  if (!keysToRemove.length) {
    keysToRemove.push(getEntryKey(documentId));
  }

  // Clear all entries from memory store
  keysToRemove.forEach((key) => {
    const entry = progressStore.get(key);
    // Clear any running timers/pollers before deleting
    if (entry) {
      if (entry.poller) {
        clearTimeout(entry.poller);
        entry.poller = null;
      }
      if (entry.optimisticTimer) {
        clearInterval(entry.optimisticTimer);
        entry.optimisticTimer = null;
      }
    }
    progressStore.delete(key);
  });

  // Clear ALL possible sessionStorage keys for this document
  sessionStorage.removeItem(getStorageKey(documentId, pipeline));
  sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${documentId}`);
  sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}unknown:${documentId}`);

  // Clear pipeline-specific keys if pipeline is provided
  if (pipeline) {
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${pipeline}:${documentId}`);
  }

  // Clear all possible pipeline combinations to ensure no stale data
  const possiblePipelines = [
    'demand_letter',
    'deposition_summary',
    'use_case',
    'unknown',
  ];
  possiblePipelines.forEach((p) => {
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${p}:${documentId}`);
  });
};

/**
 * Reset progress for a document to start a fresh generation.
 * Resets all state IN PLACE (preserving subscribers) with first task IN_PROGRESS.
 * This is called BEFORE generation starts to ensure clean state.
 */
export const resetDocumentProgress = (
  documentId: string,
  pipeline?: GenerationProgressOptions['pipeline']
) => {
  if (!documentId) return;

  // First clear ALL session storage for this document
  const possiblePipelines = [
    'demand_letter',
    'deposition_summary',
    'use_case',
    'unknown',
  ];
  possiblePipelines.forEach((p) => {
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${p}:${documentId}`);
  });
  sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${documentId}`);

  // Find and reset the entry IN PLACE (preserving subscribers)
  const existingKey = findEntryKey(documentId, pipeline);
  if (existingKey) {
    const entry = progressStore.get(existingKey);
    if (entry) {
      // Clear timers
      if (entry.poller) {
        clearTimeout(entry.poller);
        entry.poller = null;
      }
      if (entry.optimisticTimer) {
        clearTimeout(entry.optimisticTimer);
        entry.optimisticTimer = null;
      }

      // Create fresh tasks with first task IN_PROGRESS
      const freshTasks = createInitialTasks(pipeline || entry.pipeline);
      if (freshTasks.length > 0) {
        freshTasks[0] = {
          ...freshTasks[0],
          status: 'IN_PROGRESS',
          progress_percentage: 0,
          current_step: 'Starting...',
          started_at: Date.now(),
        };
      }

      // Reset all state but KEEP subscribers
      entry.tasks = freshTasks;
      entry.overallProgressFromEvent = null;
      entry.estimatedTimeFromEvent = null;
      entry.error = null;
      entry.hasProgressSnapshot = false;
      entry.hasLiveEvents = false;
      entry.finalStatus = null;
      entry.syntheticStartedAt = Date.now();
      entry.pollingDisabled = false;
      entry.failedPolls = 0;
      entry.pollDelay = undefined;
      entry.pollStartedAt = null;
      entry.runStartedAt = null;
      entry.lastSnapshot = undefined;
      entry.isFetching = false;
      // DO NOT reset entry.subscribers - keep them!

      // Notify subscribers of the reset so they get fresh state
      notifySubscribers(entry);
      return;
    }
  }

  // No existing entry - create a new one
  const freshTasks = createInitialTasks(pipeline);
  if (freshTasks.length > 0) {
    freshTasks[0] = {
      ...freshTasks[0],
      status: 'IN_PROGRESS',
      progress_percentage: 0,
      current_step: 'Starting...',
      started_at: Date.now(),
    };
  }

  const newEntry: ProgressEntry = {
    tasks: freshTasks,
    overallProgressFromEvent: null,
    estimatedTimeFromEvent: null,
    error: null,
    hasProgressSnapshot: false,
    hasLiveEvents: false,
    finalStatus: null,
    syntheticStartedAt: Date.now(),
    pipeline,
    pollingDisabled: false,
    isFetching: false,
    subscribers: new Set(),
    poller: null,
    optimisticTimer: null,
  };

  const newKey = getEntryKey(documentId, pipeline);
  progressStore.set(newKey, newEntry);
};
